import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Col } from "react-bootstrap";

import { deepEqual } from "../../../utils/object";
import { isFalseOrEmpty } from "../../../utils/string";
import { format as formatDate } from "../../../utils/date";

import { mine as myMethods, update as updateMethod, customer as updateCustomer, destroy as deleteMethod, patch as patchMethod, intent as getIntent, agreement as getAgreement, config as getStripeConfig } from "../../../actions/methods";
import { send as sendPageSetting } from "../../../actions/page";

import ConditionalButton from "../../../layout/conditional-button";
import CsLineIcons from "../../../cs-line-icons";
import DataPanel from "../../../layout/data-panel";
import Table from "../../../layout/table";
import Cards from "./cards";
import Modal from "./modal";
import Confirm from "../../../layout/modal";

class Editor extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            data: [],
            person: { ...this.props?.auth?.user?.Person },
            loading: true,
            error: "",
            view: "table",
            modal: {
                create: {
                    show: false
                },
                confirm: {
                    show: false
                }
            }
        }
    }
    componentDidUpdate = (oldProps, oldState) => {
        if (!deepEqual(oldProps, this.props)) {
            this.fetchData();
        }
    }

    componentDidMount = () => {
        this.props.sendPageSetting({ context: `payment-method-editor`, title: `Payment Methods` });
        this.fetchData();
    }

    get profilePerson() {
        return this.state.person?.person || this.state.person || this.props.auth?.user?.Person || {};
    }

    fetchData = async () => {
        this.setState({ error: "", loading: true });
        try {
            const __data = await this.props.myMethods();
            let __person = null;
            if (isFalseOrEmpty(this.profilePerson?.processorId)) {
                __person = await this.props.updateCustomer();
            }
            const data = Array.isArray(__data) ? __data : [];
            this.setState({
                data,
                error: Array.isArray(__data) ? "" : "Payment methods could not be loaded.",
                loading: false,
                person: __person?.person || this.props.auth?.user?.Person || this.profilePerson,
            });
        } catch (error) {
            this.setState({
                data: [],
                error: "Payment methods could not be loaded.",
                loading: false,
            });
        }
    };

    onSelect = (e) => {
        this.setState({
            ...this.state,
            modal: {
                ...this.state.modal,
                create: {
                    ...this.state.modal.create,
                    selected: e,
                    edit: true,
                    show: true
                }
            }
        })
    }

    onDelete = (e) => {
        this.setState({
            ...this.state,
            modal: {
                ...this.state.modal,
                confirm: {
                    ...this.state.modal.confirm,
                    show: true,
                    title: "Confirm Payment Method Deletion...",
                    body: <span>Confirm deletion of payment method with the last four digits {e.last4}?</span>,
                    uuid: e.uuid
                }
            }
        });
    }

    updateMethodOrder = async ({ uuid, order }) => {
        await this.props.patchMethod(
            {
                uuid: uuid,
                changes: [
                    {
                        resource: "method",
                        name: "order",
                        value: order
                    }
                ]
            }
        );
    }

    onOrder = async (e) => {
        const __data = this.state.data;
        let method = __data.find(method => method.order === e.order);
        if (method) {
            method.order = 0;
            this.updateMethodOrder({ uuid: method.uuid, order: 0 });
        }

        method = __data.find(method => method.uuid === e.uuid);
        method.order = e.order;
        this.updateMethodOrder({ uuid: method.uuid, order: e.order });

        this.setState({
            ...this.state,
            data: [...__data]
        })
    }

    onNewClick = async (e) => {
        const [agreement, stripeConfig] = await Promise.all([
            this.props.getAgreement(),
            this.props.getStripeConfig()
        ]);

        this.setState({
            ...this.state,
            modal: {
                ...this.state.modal,
                create: {
                    ...this.state.modal.create,
                    selected: {},
                    edit: false,
                    show: true,
                    secret: null,
                    agreement,
                    stripeConfig
                }
            }
        })
    }

    onHideConfirm = (e) => {
        this.setState({
            ...this.state,
            modal: {
                ...this.state.modal,
                confirm: {
                    ...this.state.modal.confirm,
                    show: false
                }
            }
        })
    }

    onOkConfirm = async (e) => {
        await this.props.deleteMethod({ uuid: this.state.modal.confirm.uuid });
        this.onHideConfirm();
        this.fetchData();
    }

    onHideModal = (e) => {
        this.setState({
            ...this.state,
            modal: {
                ...this.state.modal,
                create: {
                    ...this.state.modal.create,
                    show: false
                }
            }
        })
    }

    onOkMethod = async (e) => {
        const expiresDate = new Date(e.card.exp_year, e.card.exp_month - 1, 1);
        const result = await this.props.updateMethod({
            uuid: e.uuid,
            type: "card",
            address: { ...e.address },
            last4: e.card.last4,
            expiresDate,
            name: e.name,
            email: e.email,
            meta: { ...e.card },
            processorId: e.processorId,
            setupId: e.setupId,
            agreement: e.agreement,
            style: e.style
        });

        if ((result.success) || (!result.success && result.clear)) {
            this.onHideModal();
            this.fetchData();
        }
    }

    onViewChange = (view) => {
        this.setState({ view });
    }

    methodMeta = (method) => {
        if (method?.meta && typeof method.meta === "object") return method.meta;
        try {
            return JSON.parse(method?.meta || "{}");
        } catch (error) {
            return {};
        }
    }

    methodCaption = (method) => {
        const meta = this.methodMeta(method);
        const type = meta.brand || (method.accountType === "us_bank_account" ? "Bank account" : "Card");
        return `${type} ending in ${method.last4 || "----"}`;
    }

    priorityCaption = (order) => {
        if (Number(order) === 1) return "Primary";
        if (Number(order) === 2) return "Secondary";
        return "Unassigned";
    }

    get headers() {
        return [
            {
                header: "Payment Method",
                sortable: false,
                source: "accountType",
                className: "w-30",
                onRender: (_value, row) => (
                    <span className="licensee-payment-method-cell">
                        <CsLineIcons icon="credit-card" size="18" />
                        <strong>{this.methodCaption(row)}</strong>
                    </span>
                ),
            },
            {
                header: "Name",
                sortable: false,
                source: "name",
                className: "w-20",
                onRender: (value) => value || "Not provided",
            },
            {
                header: "Expires",
                sortable: false,
                source: "expiresDate",
                className: "w-20",
                onRender: (value) => value ? formatDate(value, false) : "Not applicable",
            },
            {
                header: "Priority",
                sortable: false,
                source: "order",
                className: "w-15",
                onRender: (value) => (
                    <span className={`licensee-status-pill ${Number(value) === 1 ? "is-success" : ""}`}>
                        {this.priorityCaption(value)}
                    </span>
                ),
            },
            {
                header: "",
                sortable: false,
                source: "uuid",
                className: "w-15 text-end",
                dataClassName: "text-end",
                onRender: (_value, row) => this.renderMethodActions(row),
            },
        ];
    }

    renderMethodActions = (method) => {
        const expired = method.expiresDate && new Date(method.expiresDate).getTime() < Date.now();
        return (
            <span className="licensee-payment-method-actions">
                {Number(method.order) !== 1 && !expired ? (
                    <button
                        type="button"
                        className="licensee-data-table-icon-button"
                        title="Make primary"
                        aria-label={`Make ${this.methodCaption(method)} primary`}
                        onClick={() => this.onOrder({ ...method, order: 1 })}
                    >
                        <CsLineIcons icon="check-circle" size="17" />
                    </button>
                ) : null}
                {Number(method.order) === 0 && !expired ? (
                    <button
                        type="button"
                        className="licensee-data-table-icon-button"
                        title="Make secondary"
                        aria-label={`Make ${this.methodCaption(method)} secondary`}
                        onClick={() => this.onOrder({ ...method, order: 2 })}
                    >
                        <CsLineIcons icon="arrow-top" size="17" />
                    </button>
                ) : null}
                {Number(method.order) !== 1 ? (
                    <button
                        type="button"
                        className="licensee-data-table-icon-button is-danger"
                        title="Delete payment method"
                        aria-label={`Delete ${this.methodCaption(method)}`}
                        onClick={() => this.onDelete(method)}
                    >
                        <CsLineIcons icon="bin" size="17" />
                    </button>
                ) : null}
            </span>
        );
    }

    renderCards = (data) => (
        <Cards
            data={data}
            onSelect={this.onSelect}
            onDelete={this.onDelete}
            onOrder={this.onOrder}
        />
    )

    get primaryMethod() {
        return this.state.data.find((method) => Number(method.order) === 1);
    }

    render() {
        return <>
            <Col xs="12">
                <DataPanel
                    icon="credit-card"
                    label="Saved payment methods"
                    value={this.state.data.length}
                    description={this.primaryMethod ? `${this.methodCaption(this.primaryMethod)} is primary` : "Choose a primary method for automatic billing"}
                    view={this.state.view}
                    onViewChange={this.onViewChange}
                    actions={(
                        <ConditionalButton
                            enabledVariant="primary"
                            disabledVariant="outline-muted"
                            tooltip="New Method"
                            display
                            enabled
                            onEnabledClick={this.onNewClick}
                            icon="plus"
                        >
                            New Payment Method
                        </ConditionalButton>
                    )}
                >
                    <Table
                        ariaLabel="Payment methods"
                        headers={this.headers}
                        page={this.state.data}
                        sort={{ index: -1, direction: "ASC" }}
                        size={Math.max(this.state.data.length, 1)}
                        index={0}
                        pages={this.state.data.length ? 1 : 0}
                        total={this.state.data.length}
                        loading={this.state.loading}
                        emptyTitle={this.state.error || "No payment methods yet"}
                        emptyDescription={this.state.error ? "Try refreshing this page." : "Add a payment method to use for billing."}
                        variant="licensee"
                        view={this.state.view}
                        renderCards={this.renderCards}
                    />
                </DataPanel>
            </Col>
            <Modal show={this.state.modal.create.show} onCancel={this.onHideModal} onOk={this.onOkMethod} selected={this.state.modal.create.selected} edit={this.state.modal.create.edit} intent={this.state.modal.create.secret} agreement={this.state.modal.create.agreement} publishableKey={this.state.modal.create.stripeConfig?.publishableKey} getIntent={this.props.getIntent} person={this.profilePerson}></Modal>
            <Confirm show={this.state.modal.confirm.show}
                title={this.state.modal.confirm.title || "Confirm ..."}
                body={this.state.modal.confirm.body}
                buttons={
                    [
                        {
                            default: true,
                            caption: "cancel",
                            onClick: this.onHideConfirm,
                            variant: "secondary"
                        },
                        {
                            caption: "ok",
                            onClick: this.onOkConfirm,
                            variant: "outline-primary"
                        }
                    ]
                }
            ></Confirm>
        </>
    }
}
    
Editor.propTypes = {
    auth: PropTypes.object.isRequired,
    profile: PropTypes.object
};

const mapStateToProps = (state) => {
    return ({
        auth: state.auth,
        profile: state.licensee.profile,
        page: state.page,
    });
};

export { Editor };
export default connect(mapStateToProps, { myMethods, sendPageSetting, updateMethod, updateCustomer, deleteMethod, patchMethod, getIntent, getAgreement, getStripeConfig })(Editor);
